import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import MiniAvatarCanvas from '../../components/MiniAvatarCanvas';
import type { SavedAvatarData } from '../../lib/api';
import { useActivateAvatar, useDeleteAvatar, useToggleAvatarLike } from './useAvatarCollection';

interface AvatarCardProps {
  avatar: SavedAvatarData;
  isOwner?: boolean;
}

export default function AvatarCard({ avatar, isOwner }: AvatarCardProps) {
  const navigate = useNavigate();
  const activate = useActivateAvatar();
  const remove = useDeleteAvatar();
  const toggleLike = useToggleAvatarLike();

  const handleActivate = (e: React.MouseEvent) => {
    e.stopPropagation();
    activate.mutate(avatar.id, {
      onSuccess: () => toast.success('Avatar activated!'),
      onError: () => toast.error('Failed to activate'),
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this avatar?')) return;
    remove.mutate(avatar.id, {
      onSuccess: () => toast.success('Avatar deleted'),
      onError: () => toast.error('Failed to delete'),
    });
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLike.mutate(avatar.id);
  };

  return (
    <div
      className={`malpedia-avatar-card${avatar.is_active ? ' malpedia-avatar-card--active' : ''}`}
      onClick={() => navigate(`/avatars/${avatar.id}`)}
    >
      <MiniAvatarCanvas
        config={avatar.config}
        size={112}
      />
      <span className="malpedia-avatar-card-name">{avatar.name}</span>
      {!isOwner && (
        <span className="malpedia-avatar-card-owner">by {avatar.owner}</span>
      )}

      <div className="malpedia-avatar-card-meta">
        {avatar.is_active && (
          <span className="malpedia-avatar-card-badge">Active</span>
        )}
        <button
          className={`malpedia-like-btn${avatar.user_liked ? ' malpedia-like-btn--liked' : ''}`}
          onClick={handleLike}
          title={avatar.user_liked ? 'Unlike' : 'Like'}
        >
          {avatar.user_liked ? '\u2764\uFE0F' : '\u2661'} {avatar.like_count}
        </button>
      </div>

      {isOwner && (
        <div className="malpedia-avatar-card-actions">
          {!avatar.is_active && (
            <button
              className="malpedia-avatar-card-action malpedia-avatar-card-action--star"
              onClick={handleActivate}
              title="Set as active"
            >
              Activate
            </button>
          )}
          <button
            className="malpedia-avatar-card-action malpedia-avatar-card-action--danger"
            onClick={handleDelete}
            title="Delete"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
